/**
 * MainContentBadges - Renders event or membership badges for the main content area
 *
 * Used by Hero layout to display date/time, location, price badges inline.
 * Wrapped in DetailCardContainer for consistent styling.
 *
 * Uses badges from the adapter (data.header.badges) which are populated
 * by EventBadges or MembershipBadges during the adapter's execution.
 */
import Flex from "@ui-kit/layout/Flex/Flex.web";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import type { PageData } from "../../../adapters/types";

interface MainContentBadgesProps {
  data: PageData;
}

export function MainContentBadges({
  data,
}: MainContentBadgesProps): JSX.Element | null {
  const { header } = data;
  const { badges } = header;

  // Filter to only visible badges
  const visibleBadges = badges.filter((badge) => badge.visible);

  if (visibleBadges.length === 0) return null;

  return (
    <DetailCardContainer>
      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 4 }}
        fontSize={{ base: "xs", sm: "sm" }}
        color="fg"
        spacingY={{ base: "xs", sm: "md" }}
      >
        {visibleBadges.map(({ id, dataTestId, icon, text }) => (
          <Flex
            key={id}
            mr={{ base: "none", sm: "lg" }}
            py="xs"
            alignItems={{ base: "center", sm: "flex-start" }}
            data-testid={dataTestId}
          >
            {icon} {text}
          </Flex>
        ))}
      </SimpleGrid>
    </DetailCardContainer>
  );
}
